import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, LayoutTemplate, ImageDown, SlidersHorizontal, ArrowLeft, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin/template-management", label: "Templates", icon: LayoutTemplate, need: "templates" },
  { to: "/admin/template-review", label: "Template Review", icon: ImageDown, need: "uploads" },
  { to: "/admin/pages-access", label: "Pages & Access", icon: SlidersHorizontal, need: "admin" },
];

const can = (user, need) => {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (need === "admin") return false;
  return !!user.ig_access?.[need];
};

export default function IgManageShell({ need, title, children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a]"><Loader2 className="animate-spin text-[#E1306C]" size={28} /></div>;

  if (!can(user, need)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#0a0a0a] px-6 text-center" data-testid="ig-manage-forbidden">
        <ShieldAlert size={40} className="text-[#E1306C]" />
        <h1 className="font-display text-2xl font-black text-white">Access restricted</h1>
        <p className="max-w-md text-sm text-[#A1A1AA]">{user ? "You don't have permission to open this Instagram management page. Ask the main admin to grant you access." : "Please sign in to continue."}</p>
        <button onClick={() => nav(user ? "/" : "/login")} className="mt-2 rounded-lg bg-[#E1306C] px-4 py-2 text-sm font-bold text-white">{user ? "Go home" : "Sign in"}</button>
      </div>
    );
  }

  const visible = NAV.filter((n) => can(user, n.need));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="ig-manage-shell">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0a0a0a]/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <Link to="/instagram-post-maker" className="flex items-center gap-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><ArrowLeft size={14} /> Post Maker</Link>
          <span className="hidden text-white/20 sm:inline">|</span>
          <span className="font-display text-sm font-black tracking-tight text-white">Instagram Management</span>
          <nav className="ml-auto flex flex-wrap gap-1.5" data-testid="ig-manage-nav">
            {visible.map((n) => {
              const active = loc.pathname === n.to;
              return (
                <Link key={n.to} to={n.to} data-testid={`ig-nav-${n.need}`}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold ${active ? "bg-[#E1306C] text-white" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>
                  <n.icon size={13} /> {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <h1 className="mb-5 font-display text-2xl font-black text-white" data-testid="ig-manage-title">{title}</h1>
        {children}
      </main>
    </div>
  );
}
